/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the base layout for the search view.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { useState, useCallback } from 'react';

/**
 * Utility used to generate a Hook to allow for the creation of styles using JSS.
 */
import { makeStyles } from '@material-ui/core/styles';

/**
 * Material UI Paper component.
 */
import Paper from '@material-ui/core/Paper';

/**
 * Material UI TextField component.
 */
import TextField from '@material-ui/core/TextField';

/**
 * Material UI Typography component.
 */
import { Typography, InputAdornment } from '@material-ui/core';

/**
 * Material UI Breadcrumbs component.
 */
import Breadcrumbs from '@material-ui/core/Breadcrumbs';

/**
 * Used to load items as you scroll the page.
 */
import * as InfiniteScroll from 'react-infinite-scroller';

/**
 * Used to display SVG icons.
 */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

/**
 * SVG icon definition
 */
import { faSearch } from '@fortawesome/pro-solid-svg-icons/faSearch';


/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Enables the use of version data loaded from the ServiceStack API.
 */
import { useVersionSelector, VersionSelectors } from 'src/store/reducers/version';

/**
 * Enables the use of category data loaded from the ServiceStack API.
 */
import useSearch from 'src/hooks/useSearch';

/**
 * Enumeration
 */
import { RequestState } from 'src/store/reducers';

/**
 * used to render the view for each grade item.
 */
import GradeListItem from 'src/components/GradeListItem';

/**
 * Loading indicator
 */
import Loading from 'src/components/Loading';

/**
 * used to update app state and post messages to opener window.
 */
import useAppState from 'src/hooks/useAppState';

/**
 * Enumeration
 */
import { WindowView } from 'src/utils/MessageManager';

/**
 * Typings
 */
import * as Dtos from '../api/dtos';

import TermListItem from 'src/components/TermListItem';

import CategoryListItem from 'src/components/CategoryListItem';

/*
 * ---------------------------------------------------------------------------------
 * interfaces / types
 * ---------------------------------------------------------------------------------
 */

interface ISearchProps {
    getScrollElement: () => HTMLElement | null;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles(theme => ({
    paper: {
        margin: theme.spacing(3, 0)
    },
    list: {
        width: '100%',
        padding: theme.spacing(0,0)
    },
    breadcrumbs: {
        margin: theme.spacing(3, 0)
    },
    textField: {
        width: '100%'
    },
    textResults: {
        whiteSpace: 'nowrap'
    },
    intro: {
        margin: theme.spacing(3, 0),
        padding: theme.spacing(2,2)
    },
    chip: {
        width: '100%',
        justifyContent: 'left',
        transform: 'scale(1.25)',
        transformOrigin: '0 0',

        '&': {
            width: '80%',
        }
    }
}));

const limit = 20;

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const Search: React.FunctionComponent<ISearchProps> = ({
    getScrollElement
}) => {
    const classes = useStyles();

    const currentVersion = useVersionSelector(VersionSelectors.selectVersion);

    if (!currentVersion) {
        return (
            <Loading />
        )
    }

    const [query, setQuery] = useState<string>('');

    const [ view, data, totalCount, loadState, startSearch, continueSearch ] = useSearch(currentVersion.id, limit);

    useAppState(
        WindowView.Search,
        null,
        null,
        null,
        null,
        null
    );

    const onQueryChange = useCallback((event: React.SyntheticEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, Event>) => {
        if (event.target) {
            const element: HTMLInputElement = event.target as HTMLInputElement;
            setQuery(element.value);
            startSearch(element.value);
        }
    }, [setQuery, startSearch]);

    const onLoadMore = useCallback(() => {
        continueSearch(query);
    }, [continueSearch, query]);

    const error = totalCount === 0 && query !== '';

    return (
        <>
            <Breadcrumbs
                separator="›"
                aria-label="Search"
                className={classes.breadcrumbs}
            >
                <Typography color="textPrimary">
                    Search
                </Typography>
            </Breadcrumbs>
            <TextField
                value={query}
                onChange={onQueryChange}
                margin="none"
                className={classes.textField}
                placeholder="Enter search terms..."
                error={error}
                autoFocus
                helperText={error ? `No results found for '${query}'` : undefined}
                InputProps={{
                    startAdornment: (
                        <InputAdornment
                            position="start"
                        >
                            <FontAwesomeIcon fixedWidth icon={faSearch} />
                        </InputAdornment>
                    ),
                    endAdornment: totalCount !== null ?
                        (
                            <InputAdornment
                                position="start"
                                className={classes.textResults}
                            >
                                {totalCount} Results
                            </InputAdornment>
                        ) :
                        undefined
                }}
            />
            <Paper
                className={classes.paper}
            >
                {
                    view && totalCount && totalCount !== 0 ?
                        <InfiniteScroll
                            pageStart={0}
                            initialLoad={false}
                            loadMore={onLoadMore as any}
                            hasMore={loadState.state === RequestState.Success && totalCount > view.length}
                            useWindow={false}
                            getScrollParent={getScrollElement}
                        >
                            {
                                view.map((id, index, array) => {
                                    const result = data[id];

                                    if (result.version &&
                                        result.category &&
                                        result.term &&
                                        result.grade) {
                                            const getGrade = (): [ Dtos.Grade | null] => [result.grade];

                                            return (
                                                <GradeListItem
                                                    key={`g-${id}`}
                                                    version={result.version}
                                                    category={result.category}
                                                    term={result.term}
                                                    subterm={result.subterm}
                                                    gradeId={result.grade.id}
                                                    showDivider={index !== view.length - 1}
                                                    getGrade={getGrade}
                                                    search={query}
                                                />
                                            );
                                        }

                                    if (result.version &&
                                        result.category &&
                                        result.term &&
                                        result.subterm) {
                                            const getTerm = (): [ Dtos.Term | null] => [result.subterm];

                                            return (
                                                <TermListItem
                                                    key={`st-${id}`}
                                                    version={result.version}
                                                    category={result.category}
                                                    parentTerm={result.term}
                                                    termId={result.subterm.id}
                                                    showDivider={index !== view.length - 1}
                                                    getTerm={getTerm}
                                                    search={query}
                                                />
                                            );
                                        }

                                    if (result.version &&
                                        result.category &&
                                        result.term) {
                                            const getTerm = (): [ Dtos.Term | null] => [result.term];

                                            return (
                                                <TermListItem
                                                    key={`t-${id}`}
                                                    version={result.version}
                                                    category={result.category}
                                                    termId={result.term.id}
                                                    showDivider={index !== view.length - 1}
                                                    getTerm={getTerm}
                                                    search={query}
                                                />
                                            );
                                        }

                                    if (result.version &&
                                        result.category) {
                                            const getCategory = (): [ Dtos.Category | null] => [result.category];

                                            return (
                                                <CategoryListItem
                                                    key={`t-${id}`}
                                                    version={result.version}
                                                    categoryId={result.category.id}
                                                    showDivider={index !== view.length - 1}
                                                    getCategory={getCategory}
                                                    search={query}
                                                />
                                            );
                                        }

                                    return (
                                        null
                                    )
                                })
                            }
                        </InfiniteScroll> :
                        null
                }
                {
                    loadState && loadState.state === RequestState.Pending && (
                        <Loading />
                    )
                }
            </Paper>
        </>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Exports - Default
 * ---------------------------------------------------------------------------------
 */

export default Search;


// WEBPACK FOOTER //
// ./src/containers/Search.tsx